'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useParams } from 'next/navigation';
import { localizeSpelling } from '@/lib/spelling-localization';

// Create context for spelling localization
type SpellingContextType = {
  localizeText: (text: string) => string;
  currentLocale: string;
};

const SpellingContext = createContext<SpellingContextType>({
  localizeText: (text) => text,
  currentLocale: 'en',
});

// Provider component that will wrap the app
export function SpellingProvider({ children }: { children: ReactNode }) {
  const params = useParams();
  const [currentLocale, setCurrentLocale] = useState<string>('en');
  
  // Update current locale when params change
  useEffect(() => {
    const locale = params?.locale as string || 'en';
    setCurrentLocale(locale);
    
    // Log to verify the locale is being set correctly
    console.log('Current locale for spelling localization:', locale);
  }, [params]);
  
  // Function to localize text based on current locale
  const localizeText = (text: string): string => {
    return localizeSpelling(text, currentLocale);
  };
  
  const contextValue = {
    localizeText,
    currentLocale,
  };
  
  return (
    <SpellingContext.Provider value={contextValue}>
      {children}
    </SpellingContext.Provider>
  );
}

// Custom hook to use the spelling context
export function useSpellingLocalization() {
  const context = useContext(SpellingContext);
  
  if (!context) {
    throw new Error('useSpellingLocalization must be used within a SpellingProvider');
  }
  
  return context;
} 